// Utility functions for Buzztime Bingo
import React, { useRef, useEffect } from 'react';
import { createStyledButton, injectButtonStyles } from './styles';
import { createConfetti } from './confetti';
import {
  BINGO_LINES, BLACKOUT_LINE, isBlackoutMode, getWinningLines,
  getBingoProgress, getBestBingoProgress
} from './bingoProgress';
import './variables.css'; // Import global CSS variables

/**
 * Generate a random bingo card with numbers between min and max
 * @param {number} size The size of the card (e.g., 5 for a 5x5 card)
 * @param {number} min The minimum possible number
 * @param {number} max The maximum possible number
 * @returns {Array<Array<number>>} A 2D array representing the bingo card
 */
export const generateBingoCard = (size = 5, min = 1, max = 75) => {
  const card = [];
  const usedNumbers = new Set();
  
  for (let i = 0; i < size; i++) {
    const row = [];
    for (let j = 0; j < size; j++) {
      // Center is free space in traditional bingo
      if (i === Math.floor(size/2) && j === Math.floor(size/2)) {
        row.push(0); // 0 represents free space
        continue;
      }
      
      let num;
      do {
        num = Math.floor(Math.random() * (max - min + 1)) + min;
      } while (usedNumbers.has(num));
      
      usedNumbers.add(num);
      row.push(num);
    }
    card.push(row);
  }
  
  return card;
};

/**
 * Check if a card has bingo (a complete row, column, or diagonal)
 * @param {Array<Array<number>>} card The bingo card to check
 * @param {Set<number>} markedNumbers Set of numbers that have been called
 * @returns {boolean} Boolean indicating if the card has bingo
 */
export const checkForBingo = (card, markedNumbers) => {
  const size = card.length;
  
  // Check rows
  for (let i = 0; i < size; i++) {
    let rowComplete = true;
    for (let j = 0; j < size; j++) {
      if (card[i][j] !== 0 && !markedNumbers.has(card[i][j])) {
        rowComplete = false;
        break;
      }
    }
    if (rowComplete) return true;
  }
  
  // Check columns
  for (let j = 0; j < size; j++) {
    let colComplete = true;
    for (let i = 0; i < size; i++) {
      if (card[i][j] !== 0 && !markedNumbers.has(card[i][j])) {
        colComplete = false;
        break;
      }
    }
    if (colComplete) return true;
  }
  
  // Check diagonal (top-left to bottom-right)
  let diag1Complete = true;
  for (let i = 0; i < size; i++) {
    if (card[i][i] !== 0 && !markedNumbers.has(card[i][i])) {
      diag1Complete = false;
      break;
    }
  }
  if (diag1Complete) return true;
  
  // Check diagonal (top-right to bottom-left)
  let diag2Complete = true;
  for (let i = 0; i < size; i++) {
    if (card[i][size - 1 - i] !== 0 && !markedNumbers.has(card[i][size - 1 - i])) {
      diag2Complete = false;
      break;
    }
  }
  
  return diag2Complete;
};

/**
 * Get the BINGO column letter a called ball belongs to
 * @param {number} number The called ball
 * @returns {string} The column letter, or an empty string if there is no ball
 */
export const getBallLetter = (number) => {
  if (!number) return '';

  if (number <= 15) return 'B';
  if (number <= 30) return 'I';
  if (number <= 45) return 'N';
  if (number <= 60) return 'G';
  return 'O';
};

/**
 * Get the CSS class carrying the colour for a called ball's BINGO column.
 * The colours themselves live in variables.css so every app matches.
 * @param {number} number The called ball
 * @returns {string} The class name, or an empty string if there is no ball
 */
export const getBallColorClass = (number) => {
  const letter = getBallLetter(number);
  return letter ? `ball-${letter.toLowerCase()}` : '';
};

/**
 * Format time in seconds to a human-readable format (MM:SS)
 * @param {number} seconds Number of seconds
 * @returns {string} Formatted time string
 */
export const formatTime = (seconds) => {
  if (seconds < 0) return 'N/A';
  
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Download a string as a file
 * @param {string} text Text content to download
 * @param {string} fileType File type
 * @param {string} fileName Name for the downloaded file
 */
export const downloadString = (text, fileType, fileName) => {
  const blob = new Blob([text], { type: fileType });
  const a = document.createElement('a');
  a.download = fileName;
  a.href = URL.createObjectURL(blob);
  a.dataset.downloadurl = [fileType, a.download, a.href].join(':');
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(function() { URL.revokeObjectURL(a.href); }, 2000);
};

/**
 * Convert a 2D bingo card to a flat array (for server tester compatibility)
 * @param {Array<Array<number>>} card2D The 2D bingo card
 * @returns {Array<number>} A flat array representing the bingo card
 */
export const flattenCard = (card2D) => {
  return card2D.flat();
};

/**
 * Convert a flat bingo card to a 2D array
 * @param {Array<number>} flatCard The flat bingo card
 * @param {number} size The size of the card (e.g., 5 for a 5x5 card)
 * @returns {Array<Array<number>>} A 2D array representing the bingo card
 */
export const unflattenCard = (flatCard, size = 5) => {
  const card2D = [];
  for (let i = 0; i < size; i++) {
    card2D.push(flatCard.slice(i * size, (i + 1) * size));
  }
  return card2D;
};

/**
 * Check if a flat card has bingo using the value map
 * @param {Array<number>} valueMap The flat value map (1 for marked, 0 for unmarked)
 * @param {number} size The size of the card (e.g., 5 for a 5x5 card)
 * @returns {boolean} Boolean indicating if the card has bingo
 */
export const checkForBingoWithValueMap = (valueMap, size = 5) => {
  // Check rows
  for (let i = 0; i < size; i++) {
    let rowComplete = true;
    for (let j = 0; j < size; j++) {
      const index = i * size + j;
      if (valueMap[index] !== 1) {
        rowComplete = false;
        break;
      }
    }
    if (rowComplete) return true;
  }
  
  // Check columns
  for (let j = 0; j < size; j++) {
    let colComplete = true;
    for (let i = 0; i < size; i++) {
      const index = i * size + j;
      if (valueMap[index] !== 1) {
        colComplete = false;
        break;
      }
    }
    if (colComplete) return true;
  }
  
  // Check diagonal (top-left to bottom-right)
  let diag1Complete = true;
  for (let i = 0; i < size; i++) {
    const index = i * size + i;
    if (valueMap[index] !== 1) {
      diag1Complete = false;
      break;
    }
  }
  if (diag1Complete) return true;
  
  // Check diagonal (top-right to bottom-left)
  let diag2Complete = true;
  for (let i = 0; i < size; i++) {
    const index = i * size + (size - 1 - i);
    if (valueMap[index] !== 1) {
      diag2Complete = false;
      break;
    }
  }
  
  return diag2Complete;
};

/**
 * Update a value map with a new called ball
 * @param {number} calledBall The ball that was called
 * @param {Array<number>} flatCard The flat bingo card
 * @param {Array<number>} valueMap The value map to update
 * @returns {Array<number>} The updated value map
 */
export const updateValueMap = (calledBall, flatCard, valueMap) => {
  const newValueMap = [...valueMap];
  const ballIndex = flatCard.indexOf(calledBall);
  if (ballIndex !== -1) {
    newValueMap[ballIndex] = 1;
  }
  return newValueMap;
};

/**
 * Format a message for WebSocket communication
 * @param {string} msg_type The message type
 * @param {object} body The message body
 * @returns {string} The formatted message as a JSON string
 */
export const formatWebSocketMessage = (msg_type, body) => {
  return JSON.stringify({ msg_type, body });
};

/**
 * Initialize a player saved data object
 * @returns {object} The initialized player saved data object
 */
export const initPlayerSavedData = () => {
  return {
    room_id: "",
    player_name: "",
    cards: [],
    value_maps: []
  };
};

/**
 * Initialize a host saved data object
 * @returns {object} The initialized host saved data object
 */
export const initHostSavedData = () => {
  return {
    room_id: ""
  };
};

/**
 * Creates a particle animation background
 * @param {Object} options Configuration options
 * @param {string} [options.leftColor="#00FFFF"] The color for the left side (Cyan by default)
 * @param {string} [options.rightColor="#FF69B4"] The color for the right side (Pink by default)
 * @param {number} [options.particleCount=100] The number of particles to render
 * @param {string} [options.id="particles"] The id to use for the canvas element
 * @param {HTMLElement} options.container The container element to append the canvas to
 * @returns {Object} Object containing the canvas element and cleanup function
 */
export const createParticleBackground = (options = {}) => {
  const {
    leftColor = "#00FFFF", 
    rightColor = "#FF69B4", 
    particleCount = 100,
    id = "particles",
    container = document.body
  } = options;
  
  // Create canvas element
  const canvas = document.createElement('canvas');
  canvas.id = id;
  
  // Set canvas style
  Object.assign(canvas.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    zIndex: '-1',
    background: '#121212',
    filter: 'blur(5px)',
  });
  
  // Append to container
  container.appendChild(canvas);
  
  const ctx = canvas.getContext('2d');
  
  // Parse color string to RGB object
  const parseColor = (color) => {
    // Handle hex format
    if (color.startsWith('#')) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      return { r, g, b };
    }
    // Handle rgb format
    if (color.startsWith('rgb')) {
      const matches = color.match(/\d+/g);
      if (matches && matches.length >= 3) {
        return {
          r: parseInt(matches[0], 10),
          g: parseInt(matches[1], 10),
          b: parseInt(matches[2], 10)
        };
      }
    }
    // Default fallback
    return { r: 0, g: 0, b: 0 };
  };

  // Color interpolation function
  function interpolateColor(color1, color2, factor) {
    const r = Math.round(color1.r + (color2.r - color1.r) * factor);
    const g = Math.round(color1.g + (color2.g - color1.g) * factor);
    const b = Math.round(color1.b + (color2.b - color1.b) * factor);
    return `rgb(${r}, ${g}, ${b})`;
  }

  // Define colors
  const leftColorObj = parseColor(leftColor);
  const rightColorObj = parseColor(rightColor);

  // Set canvas size
  function resizeCanvas() {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
  }
  resizeCanvas();
  
  // Listen for resize events
  window.addEventListener('resize', resizeCanvas);

  // Particle class
  class Particle {
    constructor() {
      this.reset();
    }

    reset() {
      this.x = Math.random() * canvas.width;
      this.y = Math.random() * canvas.height;
      this.size = Math.random() * 15 + 1;
      this.speedX = (Math.random() - 0.5) * 0.5;
      this.speedY = (Math.random() - 0.5) * 0.5;
      //this.opacity = Math.random() * 0.5 + 0.2;
    }

    update() {
      this.x += this.speedX;
      this.y += this.speedY;

      if (this.x < 0 || this.x > canvas.width || 
          this.y < 0 || this.y > canvas.height) {
        this.reset();
      }
    }

    draw() {
      // Calculate color based on position with smooth interpolation
      const position = this.x / canvas.width;
      const color = interpolateColor(leftColorObj, rightColorObj, position);
      
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
      const gradient = ctx.createRadialGradient(
        this.x, this.y, 0,
        this.x, this.y, this.size
      );
      gradient.addColorStop(0, color);
      gradient.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient;
      ctx.globalAlpha = this.opacity;
      ctx.fill();
    }
  }

  // Create particles
  const particles = Array(particleCount).fill().map(() => new Particle());

  // Animation loop
  let animationFrameId;
  function animate() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    particles.forEach(particle => {
      particle.update();
      particle.draw();
    });

    animationFrameId = requestAnimationFrame(animate);
  }

  // Start animation
  animate();

  // Return cleanup function
  const cleanup = () => {
    window.removeEventListener('resize', resizeCanvas);
    cancelAnimationFrame(animationFrameId);
    if (canvas.parentNode) {
      canvas.parentNode.removeChild(canvas);
    }
  };

  return { canvas, cleanup };
};

// Helper function for use in React components
export const useParticleBackground = (options = {}) => {
  useEffect(() => {
    const { cleanup } = createParticleBackground(options);
    return cleanup;
  }, [options.leftColor, options.rightColor, options.particleCount, options.id]);
};

// Re-export the button styles and confetti
export { createStyledButton, injectButtonStyles, createConfetti };
// Re-export the bingo progress helpers
export {
  BINGO_LINES, BLACKOUT_LINE, isBlackoutMode, getWinningLines,
  getBingoProgress, getBestBingoProgress
};
// Re-export the runtime-derived environment URLs
export * from './environment';
// Re-export shared hooks
export { useWebSocketHeartbeat } from './hooks/useWebSocketHeartbeat';
// Export path to variables CSS for direct imports in apps
export const variablesCssPath = require.resolve('./variables.css');

// Export shared assets
export * from '../assets';
